/// <amd-module name="Repos/FilesHubCommon/Components/FilesHeaderCommandBar" />
import * as React from "react";
import { IGitItem, IGitItemContent } from "Repos/Files/Contracts";
import * as Resources from "Repos/FilesHubCommon/Resources";
import { ObservableCollection, ObservableValue } from "VSS/Core/Observable";
import { ScreenSize } from "VSS/Core/Util/Screen";
import { HeaderCommandBar, IHeaderCommandBarItem } from "VSSUI/HeaderCommandBar";
import { Observer } from "VSSUI/Observer";
import { ITabProviderContext, TabProviderContext } from "VSSUI/Tabs";
import { IFilesHubCommonService } from "../Contracts";

export interface IFilesHeaderCommandBarProps {
    filesService: IFilesHubCommonService;
    item: IGitItem;
    saveTitle: string;
    screenSize: ScreenSize;
    webEditingEnabled?: ObservableValue<boolean>;
}

export const FilesHeaderCommandBar = (props: IFilesHeaderCommandBarProps) => {
    const { filesService, item, saveTitle, screenSize } = props;
    const webEditingEnabled = props.webEditingEnabled ?? new ObservableValue<boolean>(true);
    return (
        <Observer webEditingEnabled={webEditingEnabled} editMode={filesService.getEditMode()} fileContent={filesService.getSelectedItemContent()}>
            {(editProps: { editMode: boolean; fileContent: IGitItemContent | undefined }) => {
                const editContext = editProps.fileContent ? editProps.fileContent.editContext : undefined;
                return (
                    <TabProviderContext.Consumer>
                        {(tabProviderContext: ITabProviderContext) => {
                            return (
                                <Observer dirty={editContext ? editContext.getIsDirty() : false}>
                                    {(dirtyProps: { dirty: boolean }) => {
                                        const commandBarItems = new ObservableCollection<IHeaderCommandBarItem>();
                                        if (editProps.editMode) {
                                            if (editContext) {
                                                const disableSave = !dirtyProps.dirty && !item.isNewItem;
                                                commandBarItems.push([
                                                    {
                                                        disabled: disableSave,
                                                        iconProps: { iconName: "Save" },
                                                        id: "save",
                                                        important: true,
                                                        onActivate: () => {
                                                            editContext.commit();
                                                        },
                                                        isPrimary: !disableSave,
                                                        text: saveTitle
                                                    },
                                                    dirtyProps.dirty
                                                        ? {
                                                              iconProps: { iconName: "Undo" },
                                                              id: "revert",
                                                              important: true,
                                                              onActivate: () => {
                                                                  editContext.revert();
                                                              },
                                                              text: Resources.Revert
                                                          }
                                                        : {
                                                              iconProps: { iconName: "Cancel" },
                                                              id: "cancel",
                                                              important: true,
                                                              onActivate: () => {
                                                                  if (item.isNewItem) {
                                                                      editContext.revert();
                                                                  } else {
                                                                      filesService.setEditMode(false);
                                                                  }
                                                              },
                                                              text: Resources.Cancel
                                                          }
                                                ]);
                                            }
                                        } else {
                                            const useImportantCommands = screenSize !== ScreenSize.xsmall;
                                            if (tabProviderContext.commandBarItems) {
                                                commandBarItems.push(tabProviderContext.commandBarItems);
                                            }
                                            commandBarItems.push(filesService.getCloneMenuItems(), menuItem => {
                                                return {
                                                    ...menuItem,
                                                    important: useImportantCommands && item.path === "/" && menuItem.id === "clone"
                                                };
                                            });
                                            commandBarItems.push(
                                                filesService.getItemMenuItems(item, ["ms.vss-code-web.source-item-menu"], webEditingEnabled.value),
                                                menuItem => {
                                                    return {
                                                        ...menuItem,
                                                        important:
                                                            useImportantCommands &&
                                                            (menuItem.id === "open-in-vs" ||
                                                                (item.path !== "/" &&
                                                                    (menuItem.id === "new" ||
                                                                        menuItem.id === "edit" ||
                                                                        (menuItem.id === "download" &&
                                                                            !!(
                                                                                editProps.fileContent &&
                                                                                editProps.fileContent.details.metadata.isImage
                                                                            )))))
                                                    };
                                                }
                                            );
                                        }

                                        return (
                                            <Observer items={commandBarItems}>
                                                {(props: { items: IHeaderCommandBarItem[] }) => {
                                                    return <HeaderCommandBar className="repos-files-header-commandbar" items={props.items} />;
                                                }}
                                            </Observer>
                                        );
                                    }}
                                </Observer>
                            );
                        }}
                    </TabProviderContext.Consumer>
                );
            }}
        </Observer>
    );
};
